/**
 * Shared CMF utilities for both ProjectCMFAlignment and AIAMaterialPledge
 * Contains common types, interfaces, and helper functions for CMF impact area processing
 */

import { GetDocumentTypesResponse, SustainabilityDocument, DocumentTypesImpactArea } from '../../../../../../redux/actions/productActions/types';
import { TCImpactArea } from '../../../../../../types/impactAreas/impactAreas.types';
import { CertificationOrStandard } from '../ProjectCMF/ProjectCMFAlignment.types';

// Document impact area mapping for uploaded documents
export interface DocumentImpactAreaMapping {
    documentId: number;
    projectProductId: number;
    documentName: string;
    documentTypeId: number;
    groupId: number;
    documentTypeName: string;
    impactAreas: {
        humanHealth: boolean;
        climateHealth: boolean;
        circularEconomy: boolean;
        socialHealthEquity: boolean;
        ecosystemHealth: boolean;
    };
    impactAreaDetails: any[];
}

// Types for uploaded document structure
export interface UploadedDocument {
    id: number;
    userId: number;
    projectProductId: number;
    filePath: string;
    fileName: string | null;
    uploadedOn: string;
    expiredOn: string | null;
    size: number;
    documentName: string;
    documentTypeId: number;
    documentType?: {
        documentTypeId: number;
        orderNumber?: number;
        url: string;
        name: string;
        groupId: number;
        groupName: string;
        impactAreas: DocumentTypesImpactArea[];
        isDeleted: boolean;
    };
    alternativeText: string | null;
    description: string | null;
    createdOn: string;
    createdById: number;
    modifiedOn: string;
    modifiedById: number;
    isDeleted: boolean | null;
    cmfImpactAreas?: DocumentTypesImpactArea[];
}

export interface SustainabilityDocumentTypesInfo {
    allDocumentTypesCount?: number;
    allExistingDocumentTypesCount?: number;
    allExistingSustainabilityDocumentTypesCount?: number;
    allExistingOtherDocumentTypesCount?: number;
    documentTypes: GetDocumentTypesResponse[];
}

// Type for disclosure impact map
export type DisclosureImpactMap = Map<string, {
    humanHealth: boolean;
    climateHealth: boolean;
    circularEconomy: boolean;
    socialHealthEquity: boolean;
    ecosystemHealth: boolean;
    documentTypeName: string;
}>;

/**
 * Prepares CMF impact area mapping from uploaded documents
 * Maps each uploaded document to its CMF impact areas
 */
export const prepareCMFImpactAreaMapping = (
    uploadedDocuments: UploadedDocument[],
    sustainabilityDocumentTypesInfo: SustainabilityDocumentTypesInfo | GetDocumentTypesResponse[],
): DocumentImpactAreaMapping[] => {
    const documentTypes: GetDocumentTypesResponse[] = Array.isArray(
        sustainabilityDocumentTypesInfo,
    )
        ? sustainabilityDocumentTypesInfo
        : sustainabilityDocumentTypesInfo.documentTypes || [];

    const documentTypeMap = new Map<number, DocumentTypesImpactArea[]>();
    documentTypes.forEach((docType) => {
        if (docType?.impactAreas && docType?.impactAreas.length > 0) {
            documentTypeMap.set(docType?.documentTypeId, docType?.impactAreas);
        }
    });

    const mappings: DocumentImpactAreaMapping[] = uploadedDocuments
        .map((doc) => {
            let impactAreas: DocumentTypesImpactArea[] = [];

            if (doc?.cmfImpactAreas && doc?.cmfImpactAreas.length > 0) {
                impactAreas = doc?.cmfImpactAreas;
            } else if (
                doc?.documentType?.impactAreas &&
                doc?.documentType.impactAreas.length > 0
            ) {
                impactAreas = doc?.documentType?.impactAreas;
            } else {
                impactAreas = documentTypeMap.get(doc.documentTypeId) || [];
            }

            // Process ALL uploaded documents to determine their impact areas
            // Documents without CMF impact areas will be filtered out later

            const documentType = documentTypes.find(
                (dt) => dt?.documentTypeId === doc?.documentTypeId,
            );
            const documentTypeName = documentType?.name || doc.documentType?.name || 'Unknown';

            const impactAreaFlags = {
                humanHealth: false,
                climateHealth: false,
                circularEconomy: false,
                socialHealthEquity: false,
                ecosystemHealth: false,
            };

            // Only process impact areas if they exist
            if (impactAreas && impactAreas.length > 0) {
                impactAreas.forEach((impactArea) => {
                    const name = impactArea?.name.toLowerCase();
                    if (name.includes('human health')) {
                        impactAreaFlags.humanHealth = true;
                    } else if (name.includes('climate health')) {
                        impactAreaFlags.climateHealth = true;
                    } else if (name.includes('circular') || name.includes('circularity')) {
                        impactAreaFlags.circularEconomy = true;
                    } else if (name.includes('social health') || name.includes('equity')) {
                        impactAreaFlags.socialHealthEquity = true;
                    } else if (name.includes('ecosystem health')) {
                        impactAreaFlags.ecosystemHealth = true;
                    }
                });
            }

            return {
                documentId: doc?.id,
                projectProductId: doc?.projectProductId,
                documentName: doc?.documentName,
                documentTypeId: doc?.documentTypeId,
                documentTypeName,
                impactAreas: impactAreaFlags,
                impactAreaDetails: impactAreas,
                groupId: doc?.documentType?.groupId
            };
        })
        .filter((mapping): mapping is DocumentImpactAreaMapping => mapping !== null);

    return mappings;
};

/**
 * Processes sustainability documents from TC API and builds disclosure impact map
 * Maps disclosure types to their CMF impact areas
 */
export const buildDisclosureImpactMap = (
    sustainabilityDocuments: SustainabilityDocument[]
): DisclosureImpactMap => {
    const map: DisclosureImpactMap = new Map();

    if (!sustainabilityDocuments || !Array.isArray(sustainabilityDocuments)) {
        return map;
    }

    sustainabilityDocuments.forEach((doc: SustainabilityDocument) => {
        const impactAreas = doc.impactAreas || [];
        
        // Only process if has CMF impact areas (RULE 3)
        if (impactAreas.length === 0) {
            return;
        }

        const impactFlags = {
            humanHealth: false,
            climateHealth: false,
            circularEconomy: false,
            socialHealthEquity: false,
            ecosystemHealth: false,
            documentTypeName: doc.title || '',
        };

        impactAreas.forEach((impactArea: TCImpactArea) => {
            const title = impactArea?.title.toLowerCase();
            if (title.includes('human health')) {
                impactFlags.humanHealth = true;
            } else if (title.includes('climate health')) {
                impactFlags.climateHealth = true;
            } else if (title.includes('circular') || title.includes('circularity')) {
                impactFlags.circularEconomy = true;
            } else if (title.includes('social health') || title.includes('equity')) {
                impactFlags.socialHealthEquity = true;
            } else if (title.includes('ecosystem health')) {
                impactFlags.ecosystemHealth = true;
            }
        });

        const disclosureKey = doc.disclosureType?.toLowerCase() || '';
        if (disclosureKey) {
            map.set(disclosureKey, impactFlags);
        }
    });

    return map;
};


export const addLeedSuffix = (doc: CertificationOrStandard) => {
    if (doc.documentGroupTypeId != null && doc.documentGroupTypeId === 3)
        return "[LEED v5]";
    
    return "";
}
